import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { AIChatResponseEventFragment } from 'types/graphql'

import AIChatInputArea from 'src/components/AIChatInputArea'
import {
  CitizenAIChatMessage,
  CitizenAILaunchContext,
  CitizenAIViewMode,
  useCitizenAIAssistant,
} from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import ContentAreaOverlay from 'src/components/ContentAreaOverlay/ContentAreaOverlay'
import { getPageContext, getPageSource } from 'src/components/PageContext'
import { PageContextEnvelope } from 'src/components/PageContext/types'
import { buildHtmlPageContext } from 'src/components/PageContextHtml/build-html-page-context'
import {
  useCreateAIChatMutation,
  useLogAIChatDisabledOrgAttemptMutation,
  useSubscribeToAIChatSessionResponses,
} from 'src/fetch/aiChat'
import { useGetOrganizationForAIChatWindowQuery } from 'src/fetch/organizations'
import { Icon, T, VStack } from 'src/govwell-ui'
import { useIsInProductAICAEnabled } from 'src/utils/featureFlagging/featureFlagging'

import CitizenAIChatHeader from './CitizenAIChatHeader'
import CitizenAIChatMessageItem from './CitizenAIChatMessageItem'
import CitizenOrganizationSelector from './CitizenOrganizationSelector'
import {
  AIChatDisabledOrgAttemptSource,
  DisabledOrgAttemptSource,
  buildDisabledOrgMessage,
} from './util'

const updateLastMessage = (
  update: (message: CitizenAIChatMessage) => CitizenAIChatMessage
): React.SetStateAction<CitizenAIChatMessage[]> => {
  return (prev) => {
    const lastMessage = prev[prev.length - 1]
    if (!lastMessage) {
      return prev
    }
    return [...prev.slice(0, -1), update(lastMessage)]
  }
}

const ORG_SUGGESTION_MARKER = 'data-town-suggestion'

const ORG_SWITCH_FALLBACK_PHRASES = [
  'different municipality',
  'different jurisdiction',
  'different organization',
  'different town',
  'different county',
  'contact their staff',
  'visit their official website',
]

const responseIndicatesOrgSwitch = (text: string): boolean => {
  const lower = text.toLowerCase()
  return (
    lower.includes(ORG_SUGGESTION_MARKER) ||
    ORG_SWITCH_FALLBACK_PHRASES.some((phrase) => lower.includes(phrase))
  )
}

const DEFAULT_PROMPTS = [
  'Help me find the right form',
  'What things can you help me with?',
  'Look up my permits',
]

const GENERIC_MODE_PROMPTS = [
  'What permits do I need for a home renovation?',
  'How do I check the status of my application?',
  'What can you help me with?',
]

const FORM_CONTEXT_PROMPTS = [
  'Help me fill out this form',
  'What documents do I need for this application?',
  'What permits or fees are required?',
]

const getRecordDetailPrompts = (envelope: PageContextEnvelope | undefined): string[] => {
  if (!envelope) {
    return ['What are my next steps?', 'Who can I contact about this?']
  }

  const prompts: string[] = []

  const statusType = envelope.uiState.statusType
  const hasTasks = envelope.visibleSchema.sections.some((s) => s.title === 'Current Tasks')
  const hasUnpaidFees = envelope.visibleSchema.actions.some(
    (a) => a.actionKey === 'payFees' && a.enabled
  )

  if (statusType === 'Draft') {
    prompts.push('How do I finish and submit my application?')
    prompts.push('What information do I still need to provide?')
  } else if (statusType === 'CompleteNegative') {
    prompts.push('How can I reapply?')
    prompts.push('Who can I contact about this permit?')
  } else if (statusType === 'Complete') {
    prompts.push('How do I download or print my permit?')
    prompts.push('How do I schedule an inspection?')
  } else if (hasTasks) {
    const taskFields = envelope.visibleSchema.sections
      .filter((s) => s.title === 'Current Tasks')
      .flatMap((s) => s.fields)
    const firstTask = taskFields[0]
    if (firstTask) {
      prompts.push(`What does the "${firstTask.label}" step require?`)
    }
    prompts.push('What are my next steps for this permit?')
  } else {
    prompts.push('What are my next steps for this permit?')
    prompts.push('What does my current status mean?')
  }

  if (hasUnpaidFees) {
    prompts.push('How do I pay my fees?')
  }

  if (prompts.length < 3) {
    prompts.push('Who can I contact about this permit?')
  }

  return prompts.slice(0, 3)
}

const getPromptsForLaunchContext = (
  launchContext: CitizenAILaunchContext,
  pageContextEnvelope: PageContextEnvelope | undefined
): string[] => {
  switch (launchContext) {
    case 'applicationForm':
      return FORM_CONTEXT_PROMPTS
    case 'generic':
      return GENERIC_MODE_PROMPTS
    case 'recordDetail':
      return getRecordDetailPrompts(pageContextEnvelope)
    case 'org':
      return DEFAULT_PROMPTS
  }
}

const EMPTY_STATE_SUBTITLE_BY_LAUNCH_CONTEXT: Record<CitizenAILaunchContext, string> = {
  applicationForm:
    'Ask questions about this application form or about any other permits, fees, and requirements.',
  generic: 'Ask me anything about GovWell, permits, or government processes.',
  org: 'Ask about permits, forms, fees, and requirements.',
  recordDetail: 'Ask about the status of this permit, next steps, fees, or anything else.',
}

type CitizenAIAssistantOverlayContentProps = {
  onClose: () => void
  viewMode: CitizenAIViewMode
  onMaximize: () => void
  onMinimize: () => void
}

export const CitizenAIAssistantOverlayContent = ({
  onClose,
  viewMode,
  onMaximize,
  onMinimize,
}: CitizenAIAssistantOverlayContentProps) => {
  const context = useCitizenAIAssistant()
  const userTextInput = context?.userTextInput ?? ''
  const setUserTextInput = context?.setUserTextInput ?? (() => {})
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const messages = context?.messages ?? []
  const setMessages = context?.setMessages ?? (() => {})
  const isAssistantTyping = context?.isAssistantTyping ?? false
  const setIsAssistantTyping = context?.setIsAssistantTyping ?? (() => {})
  const entryPoint = context?.entryPoint ?? 'InProductPill'
  const launchContext = context?.launchContext ?? 'generic'
  const isGenericMode = context?.isGenericMode ?? true
  const currentOrgContext = context?.currentOrgContext
  // TODO: memoize this fallback — will be addressed in the next PR when we handle context switching more gracefully
  const setCurrentOrgContext = context?.setCurrentOrgContext ?? (() => {})
  const declinedOrgSlugs = context?.declinedOrgSlugs ?? []
  const addDeclinedOrgSlug = context?.addDeclinedOrgSlug
  const revertToGenericMode = context?.revertToGenericMode

  const chatLocalStorageId = context?.chatStorageIds.chatLocalStorageId ?? ''
  const chatSessionStorageId = context?.chatStorageIds.chatSessionStorageId ?? ''

  const { mutateAsync: createAIChat } = useCreateAIChatMutation()
  const { mutateAsync: logDisabledOrgAttempt } = useLogAIChatDisabledOrgAttemptMutation()

  const { organization } = useGetOrganizationForAIChatWindowQuery(currentOrgContext?.orgSlug ?? '')

  useEffect(() => {
    const displayName = organization?.displayName ?? organization?.city
    if (displayName && currentOrgContext?.orgSlug && !currentOrgContext.orgDisplayName) {
      setCurrentOrgContext({
        orgSlug: currentOrgContext.orgSlug,
        orgDisplayName: displayName,
        aiChatIconUrl: organization?.aiChatIconUrl ?? null,
      })
    }
  }, [organization, currentOrgContext, setCurrentOrgContext])

  useEffect(() => {
    const orgSlug = currentOrgContext?.orgSlug
    if (!orgSlug || organization?.isAIChatEnabled !== false) {
      return
    }

    const orgName = organization?.displayName ?? organization?.city ?? orgSlug

    if (!declinedOrgSlugs.includes(orgSlug)) {
      addDeclinedOrgSlug?.(orgSlug)
      setMessages((prev) => [...prev, buildDisabledOrgMessage(orgName)])
      const source: AIChatDisabledOrgAttemptSource = DisabledOrgAttemptSource.RouteNavigation
      void logDisabledOrgAttempt({ input: { orgSlug, source } }).catch(() => {})
    }

    revertToGenericMode?.()
  }, [
    organization?.isAIChatEnabled,
    organization?.displayName,
    organization?.city,
    currentOrgContext?.orgSlug,
    declinedOrgSlugs,
    addDeclinedOrgSlug,
    revertToGenericMode,
    setMessages,
    logDisabledOrgAttempt,
  ])

  const onResponseEvent = useCallback(
    (event: AIChatResponseEventFragment) => {
      if (event.__typename === 'AIChatTextUpdate') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: event.fullText,
            isThinking: !event.done,
          }))
        )
      } else if (event.__typename === 'AIChatTextDelta') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: lastMessage.text + (event.text || ''),
            isThinking: !event.done,
          }))
        )
      }
    },
    [setMessages]
  )

  useSubscribeToAIChatSessionResponses({
    chatSessionStorageId,
    onResponseEvent,
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = useCallback(
    async (autoSendText?: string) => {
      const textMessage = autoSendText ?? userTextInput
      if (!textMessage.trim() || isAssistantTyping) {
        return
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          text: textMessage,
          sender: 'user',
          questionCreatedAt: new Date().toISOString(),
        },
        {
          id: `ai-${Date.now()}`,
          text: '',
          sender: 'ai',
          isThinking: true,
        },
      ])
      setUserTextInput('')
      setIsAssistantTyping(true)

      try {
        const pageContext = getPageContext()
        const pageContextText = buildHtmlPageContext({
          viewer: 'citizen',
          structuredPageContextText: pageContext?.serialized ?? undefined,
          structuredPageContextEnvelope: pageContext?.envelope,
        })
        const result = await createAIChat({
          input: {
            chatLocalStorageId,
            chatSessionStorageId,
            orgSlug: currentOrgContext?.orgSlug || undefined,
            question: textMessage,
            source: entryPoint,
            sourceUrl: window.location.href,
            allowTownSwitching: true,
            pageContextText,
            pageSource: getPageSource('citizen'),
          },
        })

        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            id: result.createAIChat.id,
            questionCreatedAt: result.createAIChat.questionCreatedAt ?? '',
            responseCreatedAt: result.createAIChat.responseCreatedAt ?? '',
            isThinking: false,
            ...(result.createAIChat.response ? { text: result.createAIChat.response } : {}),
            ...(result.createAIChat.citations?.length
              ? { citations: result.createAIChat.citations }
              : {}),
          }))
        )
      } catch {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: '<div>Sorry, something went wrong. Please try again.</div>',
            isThinking: false,
          }))
        )
      } finally {
        setIsAssistantTyping(false)
      }
    },
    [
      userTextInput,
      isAssistantTyping,
      entryPoint,
      setMessages,
      setUserTextInput,
      setIsAssistantTyping,
      createAIChat,
      chatLocalStorageId,
      chatSessionStorageId,
      currentOrgContext?.orgSlug,
    ]
  )

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      void handleSendMessage(prompt)
    },
    [handleSendMessage]
  )

  const pageContextUrl = getPageContext()?.envelope.page.url
  const displayPrompts = useMemo(() => {
    const envelope = getPageContext()?.envelope
    return getPromptsForLaunchContext(launchContext, envelope)
  }, [launchContext, pageContextUrl])

  const hasMessages = messages.length > 0

  const lastAIMessage = useMemo(
    () => [...messages].reverse().find((msg) => msg.sender === 'ai'),
    [messages]
  )
  const hasDeclinedOrgs = declinedOrgSlugs.length > 0
  const showOrgSelectorAfterMessages =
    !isAssistantTyping &&
    !hasDeclinedOrgs &&
    !!lastAIMessage &&
    !lastAIMessage.isThinking &&
    responseIndicatesOrgSwitch(lastAIMessage.text)

  const emptyStateIconUrl = currentOrgContext?.aiChatIconUrl ?? undefined

  const SideViewEmptyState = useMemo(
    () => (
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
        {emptyStateIconUrl ? (
          <img
            src={emptyStateIconUrl}
            alt=""
            className="mb-4 h-14 w-14 rounded-full object-cover"
          />
        ) : (
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-900">
            <Icon.FaSparkles className="h-7 w-7 text-white" />
          </div>
        )}
        <T.Body className="mb-1 text-center font-semibold text-neutral-900">
          {isGenericMode || !currentOrgContext?.orgDisplayName
            ? 'GovWell AI Assistant'
            : `${currentOrgContext.orgDisplayName} Assistant`}
        </T.Body>
        <T.BodySmall className="mb-4 text-center text-neutral-600">
          {EMPTY_STATE_SUBTITLE_BY_LAUNCH_CONTEXT[launchContext]}
        </T.BodySmall>
      </div>
    ),
    [isGenericMode, launchContext, currentOrgContext?.orgDisplayName, emptyStateIconUrl]
  )

  return (
    <div className="flex h-full bg-neutral-50">
      <div className="flex min-w-0 flex-1 flex-col">
        <CitizenAIChatHeader
          onClose={onClose}
          viewMode={viewMode}
          onMaximize={onMaximize}
          onMinimize={onMinimize}
          showSidebar={false}
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {!hasMessages ? (
            SideViewEmptyState
          ) : (
            <div className="mx-auto w-full flex-1 px-4 py-4">
              <VStack gap={4} align="stretch">
                {messages.map((msg) => (
                  <CitizenAIChatMessageItem
                    key={msg.id}
                    message={msg}
                    onFollowUpClick={handleSelectPrompt}
                  />
                ))}
                {showOrgSelectorAfterMessages && <CitizenOrganizationSelector />}
                <div ref={messagesEndRef} />
              </VStack>
            </div>
          )}
        </div>

        <AIChatInputArea
          value={userTextInput}
          onValueChange={setUserTextInput}
          onSend={() => handleSendMessage()}
          isDisabled={isAssistantTyping}
          suggestedPrompts={displayPrompts}
          onSelectPrompt={handleSelectPrompt}
          showPrompts={!hasMessages}
        />
      </div>
    </div>
  )
}

const CitizenAIAssistantOverlay = () => {
  const isInProductAICAEnabled = useIsInProductAICAEnabled()
  const context = useCitizenAIAssistant()

  if (!isInProductAICAEnabled || !context) {
    return null
  }

  const { viewMode, overlay, deferredClose, maximize, minimize } = context

  if (viewMode === 'side') {
    return null
  }

  return (
    <ContentAreaOverlay isOpen={overlay.isOpen} onClose={deferredClose}>
      <CitizenAIAssistantOverlayContent
        onClose={overlay.close}
        viewMode={viewMode}
        onMaximize={maximize}
        onMinimize={minimize}
      />
    </ContentAreaOverlay>
  )
}

export default React.memo(CitizenAIAssistantOverlay)
